import apiClient, { api } from './client';
import type { Platform } from './types';

export type AccountPermissionScope =
  | 'campaign_read'
  | 'campaign_write'
  | 'campaign_copy'
  | 'dashboard_read'
  | 'dashboard_export'
  | 'platform_account_read'
  | 'platform_account_manage';

// ============================================
// Account Types
// ============================================

export interface ListAccountsParams {
  brand_id?: string;
  site_id?: string;
  platform?: Platform;
  status?: string;
  scope?: AccountPermissionScope;
  page?: number;
  size?: number;
}

export interface PlatformAccount {
  id: string;
  user_id: string;
  user_name?: string;
  brand_id: string;
  site_id?: string;
  platform: Platform;
  account_id: string;
  account_name: string;
  status: string;
  metadata?: unknown;
  api_endpoint?: string;
  api_version?: string;
  token_expiry?: string;
  last_synced_at?: string;
  sync_status: 'idle' | 'syncing' | 'success' | 'failed' | string;
  sync_started_at?: string;
  sync_finished_at?: string;
  sync_message?: string;
  created_at: string;
  updated_at?: string;
  is_mine?: boolean;
}

export interface AccountListResponse {
  data: PlatformAccount[];
  total: number;
  page: number;
  size: number;
}

export interface BindAccountRequest {
  brand_id?: string;
  site_id?: string;
  platform: Platform;
  redirect_uri: string;
}

export interface EntityInsights {
  spend: number;
  roas: number;
  cpc: number;
  cpm: number;
  cpa: number;
  purchase_count: number;
  cta_registration?: number;
  cta_purchase?: number;
  cta_purchase_value?: number;
  cta_purchase_roas?: number;
}

export interface FlatListSummary extends EntityInsights {
  row_count: number;
  active_count?: number;
}

export interface DraftPayloadRequest {
  site_id?: string;
  platform: 'facebook' | 'tiktok' | 'google';
  scene: 'create' | 'edit';
  entity_type: string;
  entity_id?: number;
  draft_key: string;
  draft_name?: string;
  payload: Record<string, unknown>;
}

export interface DraftPayloadResponse {
  mode: 'manual' | 'temp';
  draft_key: string;
  version_no?: number;
  updated_at?: string;
  payload?: Record<string, unknown>;
}

export interface SavedViewDraftPayload {
  statusFilter: string;
  selectedAccountIDs: string[];
  metricsDateRange: string;
  customStartDate: string;
  customEndDate: string;
  objectiveFilter: string;
  bidStrategyFilter: string;
  metricFilters: MetricFilterRule[];
  sortField: (keyof EntityInsights | 'cta_registration' | 'cta_purchase' | 'cta_purchase_value' | 'cta_purchase_roas') | null;
  sortDir: 'asc' | 'desc';
}

export interface SavedViewItemResponse {
  name: string;
  draft: SavedViewDraftPayload;
  updated_at?: string;
}

export interface BindAccountResponse {
  oauth_url: string;
  state: string;
}

export interface OAuthCallbackParams {
  code?: string;
  state?: string;
  error?: string;
  error_reason?: string;
}

export interface OAuthSelectableAccount {
  account_id: string;
  account_name: string;
}

export interface FacebookAccountOption {
  id: string;
  user_id: string;
  user_name: string;
  account_id: string;
  account_name: string;
  is_mine?: boolean;
}

export interface FacebookTemplateAdSet {
  id: number;
  third_ad_set_id: string;
  name: string;
  status: number;
  updated_at?: string;
}

export interface FacebookCampaignTemplate {
  id: number;
  third_ad_campaign_id: string;
  account_id: string;
  account_name: string;
  name: string;
  status: number;
  updated_at?: string;
  adset_count: number;
  ad_count: number;
  ad_sets: FacebookTemplateAdSet[];
  template_ad_copy?: string;
  template_cta?: string;
  template_landing_url?: string;
  template_material_asset_id?: string;
  template_material_name?: string;
  template_material_type?: string;
  template_material_fb_id?: string;
}

export interface FacebookTemplateListResponse {
  list: FacebookCampaignTemplate[];
  total: number;
  page: number;
  size: number;
  total_page: number;
}

export interface CreateFacebookAdRequest {
  account_id: string;
  template_campaign_id: number;
  campaign_id: number;
  ad_set_id: number;
  ad_name: string;
  ad_copy?: string;
  cta?: string;
  landing_url?: string;
  material_asset_id?: string;
  site_id?: string;
}

export interface CreateFacebookAdResponse {
  id: number;
  third_ad_id: string;
  account_id: string;
  campaign_id: number;
  ad_set_id: number;
  name: string;
  status: number;
  publish_status?: 'pending' | 'published' | 'failed' | string;
  publish_error?: string;
  created_at: string;
}

export interface FacebookMaterial {
  id: string;
  account_id: string;
  material_type: string;
  material_name: string;
  facebook_media_id: string;
  thumb_hash?: string;    // auto-generated thumbnail image_hash for video materials
  video_ready?: boolean;  // true = Facebook confirmed video encoding is complete, safe to publish
  preview_url?: string;
  source: string;
  created_at?: string;
}

export interface FacebookMaterialListResponse {
  list: FacebookMaterial[];
  total: number;
  page: number;
  size: number;
  total_page: number;
}

export interface TikTokMaterial {
  id: string;
  advertiser_id: string;
  material_type: string;
  material_name: string;
  tiktok_media_id: string;
  created_at?: string;
}

export interface TikTokMaterialListResponse {
  list: TikTokMaterial[];
  total: number;
  page: number;
  size: number;
  total_page: number;
}

export interface OAuthCallbackResponse {
  requires_selection: boolean;
  selection_token?: string;
  accounts?: OAuthSelectableAccount[];
  account?: PlatformAccount;
  message?: string;
}

export interface FacebookSelectAccountRequest {
  selection_token: string;
  account_ids: string[];
}

export interface FacebookSelectAccountResponse {
  accounts: PlatformAccount[];
  message?: string;
}

export interface RefreshAccountResponse {
  success: boolean;
  new_expiry?: string;
  error?: string;
}

// ============================================
// Accounts API
// ============================================

export interface CampaignFlatNode {
  id: number;
  third_ad_campaign_id: string;
  platform_account_id: string;
  platform: string;
  name: string;
  status: number;
  effective_status?: string;
  secondary_status?: string;
  issues_info?: FacebookIssuesInfo[] | null;
  publish_status: string;
  publish_error?: string;
  updated_at?: string;
  ad_set_count?: number;
  ad_count?: number;
  objective?: string;
  budget_scope?: string;
  budget_strategy?: string;
  adset_budget_mode?: string;
  adset_budget?: number;
  daily_budget?: number;
  lifetime_budget?: number;
  spend_cap?: number;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
  // Inline insights
  spend?: number;
  roas?: number;
  cpc?: number;
  cpm?: number;
  cpa?: number;
  purchase_count?: number;
  cta_registration?: number;
  cta_purchase?: number;
  cta_purchase_value?: number;
  cta_purchase_roas?: number;
}

export interface AdSetFlatNode {
  id: number;
  third_ad_set_id: string;
  campaign_id: number;
  platform_account_id?: string;
  platform?: string;
  campaign_name?: string;
  name: string;
  status: number;
  effective_status?: string;
  secondary_status?: string;
  issues_info?: FacebookIssuesInfo[] | null;
  publish_status: string;
  publish_error?: string;
  updated_at?: string;
  ad_count?: number;
  budget_type?: string;
  daily_budget?: number;
  lifetime_budget?: number;
  adset_budget_mode?: string;
  adset_budget?: number;
  bid_strategy?: string;
  optimization_goal?: string;
  optimization_event?: string;
  destination_type?: string;
  start_time?: string;
  end_time?: string;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
  // Inline insights
  spend?: number;
  roas?: number;
  cpc?: number;
  cpm?: number;
  cpa?: number;
  purchase_count?: number;
  cta_registration?: number;
  cta_purchase?: number;
  cta_purchase_value?: number;
  cta_purchase_roas?: number;
}

export interface AdFlatNode {
  id: number;
  third_ad_id: string;
  adset_id: number;
  platform_account_id?: string;
  platform?: string;
  adset_name?: string;
  campaign_id?: number;
  name: string;
  status: number;
  effective_status?: string;
  secondary_status?: string;
  issues_info?: FacebookIssuesInfo[] | null;
  publish_status?: 'pending' | 'published' | 'failed' | string;
  publish_error?: string;
  updated_at?: string;
  image_url?: string;
  video_url?: string;
  // Ad copy content
  headline?: string;
  primary_text?: string;
  description?: string;
  cta_type?: string;
  link?: string;
  url_tags?: string;
  landing_page_url?: string;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
  // Inline insights
  spend?: number;
  roas?: number;
  cpc?: number;
  cpm?: number;
  cpa?: number;
  purchase_count?: number;
  cta_registration?: number;
  cta_purchase?: number;
  cta_purchase_value?: number;
  cta_purchase_roas?: number;
}

export interface FacebookIssuesInfo {
  level?: string | number;
  error_type?: string;
  error_summary?: string;
  error_message?: string;
  error_code?: string | number;
}

export interface CopyFacebookCampaignRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
}

export interface CopyFacebookAdSetRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
  target_campaign_id: number;
}

export interface CopyFacebookAdRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
  target_adset_id: number;
}

export interface BatchUpdateFacebookTagsRequest {
  brand_id: string;
  site_id?: string;
  entity: 'campaign' | 'adset' | 'ad';
  ids: number[];
  tags: Record<string, string>;
  campaign_tags?: Record<string, string>;
  adset_tags?: Record<string, string>;
  ad_tags?: Record<string, string>;
}

export interface BatchUpdateFacebookTagsResponse {
  updated_count: number;
}

export interface CopyGoogleCampaignRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
}

export interface CopyGoogleAdSetRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
  target_campaign_id: number;
}

export interface CopyGoogleAdRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
  target_adset_id: number;
}

export interface CopyTikTokCampaignRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
}

export interface CopyTikTokAdSetRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
  target_campaign_id: number;
}

export interface CopyTikTokAdRequest {
  brand_id: string;
  site_id?: string;
  target_account_id: string;
  target_adset_id: number;
}

export interface FacebookPublishSummary {
  entity: 'campaign' | 'adset' | 'ad';
  id: number;
  campaigns_published: number;
  adsets_published: number;
  ads_published: number;
}

export interface FacebookEditCampaignPayload {
  name: string;
  campaign_objective?: string;
  campaign_status?: string;
  campaign_budget_scope?: 'campaign' | 'adset';
  bid_strategy?: string;
  daily_budget: number;
  campaign_lifetime_budget: number;
  spend_cap: number;
  special_ad_categories: string[];
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface FacebookEditAdSetPayload {
  name: string;
  budget_type: string;
  adset_daily_budget: number;
  adset_lifetime_budget: number;
  billing_event: string;
  optimization_goal: string;
  destination_type?: string;
  bid_strategy: string;
  bid_amount: number;
  bid_constraints_roas?: number;
  start_time: string;
  end_time: string;
  pixel_id?: string;
  custom_event_type?: string;
  product_catalog_id?: string;
  product_set_id?: string;
  countries: string[];
  excluded_countries?: string[];
  genders: number[];
  age_min: number;
  age_max: number;
  interests?: { id: string; name: string }[];
  behaviors?: { id: string; name: string }[];
  custom_audience_ids: string[];
  excluded_custom_audience_ids: string[];
  publisher_platforms: string[];
  facebook_positions: string[];
  instagram_positions: string[];
  device_platforms: string[];
  user_os: string[];
  adset_status?: string;
  frequency_cap?: number;
  frequency_cap_reset_period?: number;
  attribution_spec?: string;
  is_incremental_attribution_enabled?: boolean;
  locales?: number[];
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface FacebookEditAdPayload {
  name: string;
  ad_status?: string;
  ad_type: string;
  page_id: string;
  instagram_actor_id?: string;
  image_hash: string;
  image_url?: string;
  video_id: string;
  video_url?: string;
  thumb_hash: string;
  lead_gen_form_id?: string;
  app_id?: string;
  app_store_url?: string;
  message: string;
  headline: string;
  description: string;
  link: string;
  caption: string;
  cta_type: string;
  url_tags: string;
  creative_feature_enrollments?: Record<string, 'OPT_IN' | 'OPT_OUT' | string>;
  offer_enabled?: boolean;
  offer_source?: 'auto' | 'manual' | string;
  offer_code?: string;
  offer_description?: string;
  offer_email_subscription_enabled?: boolean;
  offer_card_image_hash?: string;
  // Carousel
  carousel_cards?: CarouselCardRequest[];
  multi_share_optimized?: boolean;
  multi_share_end_card?: boolean;
  // Dynamic creative (Advantage+)
  dyn_bodies?: string[];
  dyn_titles?: string[];
  dyn_descs?: string[];
  dyn_ctas?: string[];
  dyn_images?: string[];
  dyn_videos?: string[];
  dyn_link_url?: string;
  dyn_display_url?: string;
  dyn_url_tags?: string;
  // Existing creative
  existing_creative_id?: string;
  existing_post_platform?: 'facebook' | 'instagram';
  existing_post_id?: string;
  existing_post_page_id?: string;
  // Partnership
  partnership_code?: string;
  partnership_ig_user_id?: string;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface FacebookEditDetailResponse {
  entity: 'campaign' | 'adset' | 'ad';
  editable_fields: string[];
  sync_mode?: 'local' | 'remote';
  campaign?: FacebookEditCampaignPayload & { id: number; account_id: string };
  adset?: FacebookEditAdSetPayload & { id: number; campaign_id: number };
  ad?: FacebookEditAdPayload & {
    id: number; adset_id: number;
    carousel_cards_json?: string;
    multi_share_optimized?: boolean;
    multi_share_end_card?: boolean;
    dyn_bodies_json?: string;
    dyn_titles_json?: string;
    dyn_descs_json?: string;
    dyn_ctas_json?: string;
    dyn_images_json?: string;
    dyn_videos_json?: string;
    existing_creative_id?: string;
    partnership_code?: string;
    partnership_ig_user_id?: string;
  };
}

export interface FlatListResponse<T> {
  list: T[];
  total: number;
  page: number;
  size: number;
  total_page: number;
  summary?: FlatListSummary;
}

export interface FacebookAdEntityOption {
  id: string;
  name: string;
}

export type MetricFilterRule = {
  metric: 'spend' | 'roas' | 'cpc' | 'cpm' | 'cpa' | 'purchase_count' | 'cta_registration' | 'cta_purchase' | 'cta_purchase_value' | 'cta_purchase_roas';
  operator: 'gt' | 'lt' | 'between';
  value: number;
  value2?: number;
};

// ── Full Campaign + AdSet + Ad creation ─────────────────────────────────────

export interface FacebookPage {
  id: string;
  name: string;
}

export interface FacebookPixel {
  id: string;
  name: string;
}

export interface FacebookCatalog {
  id: string;
  name: string;
}

export interface FacebookProductSet {
  id: string;
  name: string;
  product_count?: number;
}

export interface FacebookInterest {
  id: string;
  name: string;
  audience_size_low?: number;
}

export interface FacebookGeoLocation {
  key: string;
  name: string;
  type: string;
  country_code?: string;
}

export interface FacebookBehavior {
  id: string;
  name: string;
  description?: string;
}

export interface FacebookInstagramAccount {
  id: string;
  name: string;
  username: string;
}

export interface FacebookPartnershipPage {
  id: string;
  name: string;
  status?: string;
  status_label?: string;
  selectable?: boolean;
}

export interface FacebookPartnershipInstagramAccount {
  id: string;
  name: string;
  username: string;
  status?: string;
  status_label?: string;
  selectable?: boolean;
}

export interface FacebookPartnershipInfo {
  code: string;
  status: string;
  actor_id?: string;
  actor_name: string;
  media_url?: string;
  media_id?: string;
  permalink_url?: string;
  catalog_id?: string;
  catalog_name?: string;
}

export interface FacebookExistingPost {
  platform: 'facebook' | 'instagram';
  id: string;
  page_id?: string;
  ig_user_id?: string;
  title?: string;
  message?: string;
  link?: string;
  cta_type?: string;
  thumbnail_url?: string;
  permalink_url?: string;
  source?: string;
  media_type?: string;
  created_time?: string;
  like_count?: number;
  comment_count?: number;
  share_count?: number;
  website_compatible?: boolean;
}

export interface FacebookCustomAudience {
  id: string;
  name: string;
  description?: string;
  subtype?: string;
  approximate_count?: number;
}

export interface CarouselCardRequest {
  image_hash?: string;
  video_id?: string;
  headline?: string;
  description?: string;
  link?: string;
  cta_type?: string;
}

export interface CreateFacebookFullRequest {
  account_id: string;
  // Campaign
  campaign_name: string;
  campaign_objective?: string;
  campaign_status?: string;
  daily_budget?: number; // USD — campaign-level (CBO)
  // AdSet
  adset_name: string;
  budget_type?: 'daily' | 'lifetime';
  adset_daily_budget?: number;    // USD
  adset_lifetime_budget?: number; // USD
  billing_event?: string;
  optimization_goal?: string;
  destination_type?: string;
  genders?: number[];
  countries?: string[];
  excluded_countries?: string[];
  age_min?: number;
  age_max?: number;
  interests?: { id: string; name: string }[];
  custom_audience_ids?: string[];
  pixel_id?: string;
  custom_event_type?: string;
  product_catalog_id?: string;
  product_set_id?: string;
  bid_strategy?: string;
  bid_amount?: number; // USD
  start_time?: string;
  end_time?: string;
  // Ad
  ad_name: string;
  ad_status?: string;
  page_id?: string;
  ad_type?: string; // image | video | carousel | catalog
  // Image/Video
  image_hash?: string;
  image_url?: string;
  video_id?: string;
  video_url?: string;
  thumb_hash?: string;
  // Common creative
  message?: string;
  headline?: string;
  description?: string;
  link?: string;
  caption?: string;
  cta_type?: string;
  url_tags?: string;
  creative_feature_enrollments?: Record<string, 'OPT_IN' | 'OPT_OUT' | string>;
  offer_enabled?: boolean;
  offer_source?: 'auto' | 'manual' | string;
  offer_code?: string;
  offer_description?: string;
  offer_email_subscription_enabled?: boolean;
  offer_card_image_hash?: string;
  // Carousel
  carousel_cards?: CarouselCardRequest[];
  multi_share_optimized?: boolean;
  multi_share_end_card?: boolean;
  // Dynamic
  dyn_bodies?: string[];
  dyn_titles?: string[];
  dyn_descs?: string[];
  dyn_ctas?: string[];
  dyn_images?: string[];
  dyn_videos?: string[];
  dyn_link_url?: string;
  dyn_display_url?: string;
  dyn_url_tags?: string;
  // Existing creative
  existing_creative_id?: string;
  existing_post_platform?: 'facebook' | 'instagram';
  existing_post_id?: string;
  existing_post_page_id?: string;
  // Instagram actor (advertiser's own linked Instagram account)
  instagram_actor_id?: string;
  threads_profile_id?: string;
  // Partnership (Branded Content) — Instagram post shortcode from creator
  partnership_code?: string;
  partnership_ig_user_id?: string; // creator's IG user ID (optional, improves creative resolution)
  // Legacy template-based
  template_campaign_id?: number;
  ad_copy?: string;
  cta?: string;
  landing_url?: string;
  material_asset_id?: string;
  // Campaign extensions
  spend_cap?: number;                      // USD
  campaign_lifetime_budget?: number;       // USD（广告系列级终生预算）
  // AdSet extensions
  adset_status?: string;                   // PAUSED | ACTIVE
  frequency_cap?: number;                  // 频次上限（0=不限）
  frequency_cap_reset_period?: number;     // 重置天数（默认 7）
  attribution_spec?: string;               // 归因窗口
  is_incremental_attribution_enabled?: boolean; // 增量归因
  excluded_custom_audience_ids?: string[]; // 排除受众
  publisher_platforms?: string[];          // 版位平台
  facebook_positions?: string[];           // Facebook 版位
  instagram_positions?: string[];          // Instagram 版位
  device_platforms?: string[];             // mobile | desktop
  user_os?: string[];                      // iOS | Android
  behaviors?: { id: string; name: string }[];
  locales?: number[];                      // 语言（Facebook locale ID）
  bid_constraints_roas?: number;           // ROAS 底线（LOWEST_COST_WITH_MIN_ROAS 时使用）
  // Ad extensions
  lead_gen_form_id?: string;
  app_id?: string;
  app_store_url?: string;
  // 复用已有层级（新增组 / 新增广告场景）
  existing_campaign_id?: number; // > 0 时跳过 campaign 创建
  existing_adset_id?: number;    // > 0 时跳过 campaign+adset 创建
  // 广告标签（动态 key-value，分层级）
  tags?: Record<string, string>;
  adset_tags?: Record<string, string>;
  ad_tags?: Record<string, string>;
}

export interface CreateFacebookFullResponse {
  campaign_local_id: number;
  campaign_third_id: string;
  campaign_name: string;
  campaign_error?: string;
  adset_local_id: number;
  adset_third_id: string;
  adset_name: string;
  adset_error?: string;
  ad: {
    id: number;
    third_ad_id: string;
    name: string;
    publish_status?: string;
    publish_error?: string;
  };
}

export interface FacebookCreateConfigResponse {
  default_objective: string;
  campaign_objectives: string[];
  goal_matrix: Record<string, string[]>;
  default_goal_by_objective: Record<string, string>;
}

// ── 通用多平台创建接口类型 ────────────────────────────────────────────────────

export type AdPlatform = 'facebook' | 'tiktok' | 'google';

/** 通用创建请求：platform 字段 + 平台各自的字段（扁平合并到同一个对象） */
export type CreateAdFullRequest = { platform: AdPlatform } & Record<string, unknown>;

/** 通用验证请求：同上 */
export type ValidateAdFullRequest = { platform: AdPlatform } & Record<string, unknown>;

/** 通用创建/验证返回（后端 AdCreateFullResult / AdValidateResult） */
export interface AdCreateFullResult {
  platform: AdPlatform;
  campaign_local_id: number;
  campaign_third_id: string;
  campaign_name: string;
  campaign_error?: string;
  adset_local_id: number;
  adset_third_id: string;
  adset_name: string;
  adset_error?: string;
  ad?: {
    id: number;
    third_ad_id: string;
    name: string;
    publish_status?: string;
    publish_error?: string;
  };
}

export interface AdValidateResult {
  valid: boolean;
  campaign_error?: string;
  adset_error?: string;
  creative_error?: string;
}

export const accountsApi = {
  list: (params?: ListAccountsParams) =>
    api.get<AccountListResponse>('/accounts', { params }),

  bind: (data: BindAccountRequest) =>
    api.post<BindAccountResponse>('/accounts/bind', data),

  oauthCallback: (params: OAuthCallbackParams) =>
    api.get<OAuthCallbackResponse>('/oauth/callback', { params }),

  selectFacebookAccount: (data: FacebookSelectAccountRequest) =>
    api.post<FacebookSelectAccountResponse>('/accounts/facebook/select', data, { timeout: 120000 }),

  refresh: (id: string) =>
    api.post<RefreshAccountResponse>(`/accounts/${id}/refresh`),

  triggerSync: (id: string) =>
    api.post<{ success: boolean; message?: string }>(`/accounts/${id}/sync`),

  delete: (id: string) =>
    api.delete<void>(`/accounts/${id}`),

  listFacebookOptions: (brandID: string, siteID?: string, scope?: AccountPermissionScope) =>
    api.get<{ list: FacebookAccountOption[] }>('/accounts/facebook/options', { params: { brand_id: brandID, site_id: siteID, scope } }),

  getFacebookCreateConfig: () =>
    api.get<FacebookCreateConfigResponse>('/accounts/facebook/create-config'),

  listFacebookTemplates: (params: { brand_id: string; site_id?: string; account_id: string; search?: string; page?: number; size?: number }) =>
    api.get<FacebookTemplateListResponse>('/accounts/facebook/templates', { params }),

  listFacebookMaterials: (params: { brand_id: string; site_id?: string; account_id: string; template_campaign_id?: number; search?: string; page?: number; size?: number }) =>
    api.get<FacebookMaterialListResponse>('/accounts/facebook/materials', { params }),

  uploadFacebookMaterial: (brandID: string, data: FormData, onProgress?: (pct: number) => void) =>
    apiClient.post<FacebookMaterial>(`/accounts/facebook/materials/upload?brand_id=${encodeURIComponent(brandID)}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 min — video uploads can be large
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
        : undefined,
    }).then((res) => res.data),

  uploadTikTokMaterial: (brandID: string, data: FormData, onProgress?: (pct: number) => void) =>
    apiClient.post<TikTokMaterial>(`/accounts/tiktok/materials/upload?brand_id=${encodeURIComponent(brandID)}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
        : undefined,
    }).then((res) => res.data),

  listTikTokMaterials: (params: { brand_id: string; advertiser_id: string; material_type?: string; search?: string; page?: number; size?: number }) =>
    api.get<TikTokMaterialListResponse>('/accounts/tiktok/materials', { params }),

  listTikTokIdentities: (brandID: string, advertiserID: string, siteID?: string) =>
    apiClient
      .get<{ items: TikTokIdentityOption[] }>(
        `/accounts/tiktok/identities?brand_id=${encodeURIComponent(brandID)}&advertiser_id=${encodeURIComponent(advertiserID)}${siteID ? `&site_id=${encodeURIComponent(siteID)}` : ''}`
      )
      .then((res) => res.data),

  createFacebookAd: (brandID: string, data: CreateFacebookAdRequest) =>
    api.post<CreateFacebookAdResponse>(`/accounts/facebook/ads?brand_id=${encodeURIComponent(brandID)}`, data),

  retryFacebookAdPublish: (brandID: string, adID: number, siteID?: string) =>
    api.post<CreateFacebookAdResponse>(`/accounts/facebook/ads/${adID}/retry?brand_id=${encodeURIComponent(brandID)}${siteID ? `&site_id=${encodeURIComponent(siteID)}` : ''}`),

  deleteLocalFacebookAd: (brandID: string, adID: number) =>
    api.delete<{ success: boolean }>(`/accounts/facebook/ads/${adID}?brand_id=${encodeURIComponent(brandID)}`),

  deleteFacebookEntity: (brandID: string, entity: 'campaign' | 'adset' | 'ad', id: number, siteID?: string) =>
    api.post<{ success: boolean }>('/accounts/facebook/delete', { entity, id, brand_id: brandID, site_id: siteID }),

  deleteTikTokEntity: (brandID: string, entity: 'campaign' | 'adset' | 'ad', id: number, siteID?: string) =>
    api.post<{ success: boolean }>('/accounts/tiktok/delete', { entity, id, brand_id: brandID, site_id: siteID }),

  listFacebookCampaigns: (params: { brand_id: string; site_id?: string; account_id?: string; status_filter?: string; search?: string; page?: number; size?: number; platform?: 'all' | 'facebook' | 'google' | 'tiktok'; objective?: string; bid_strategy?: string; metric_filters?: string; start_date?: string; end_date?: string; sort_by?: string; sort_dir?: 'asc' | 'desc' }) =>
    api.get<FlatListResponse<CampaignFlatNode>>('/accounts/facebook/campaigns', { params }),

  listFacebookAdEntityOptions: (params: { brand_id: string; site_id?: string; entity: 'campaign' | 'adset' | 'ad'; search?: string; page?: number; size?: number }) =>
    api.get<FlatListResponse<FacebookAdEntityOption>>('/accounts/facebook/ad-entity-options', { params }),

  listTikTokAdEntityOptions: (params: { brand_id: string; site_id?: string; entity: 'campaign' | 'adset' | 'ad'; search?: string; page?: number; size?: number }) =>
    api.get<FlatListResponse<FacebookAdEntityOption>>('/accounts/tiktok/ad-entity-options', { params }),

  listFacebookAdSets: (params: { brand_id: string; site_id?: string; account_id?: string; campaign_id?: number; status_filter?: string; search?: string; page?: number; size?: number; platform?: 'all' | 'facebook' | 'google' | 'tiktok'; bid_strategy?: string; metric_filters?: string; start_date?: string; end_date?: string; sort_by?: string; sort_dir?: 'asc' | 'desc' }) =>
    api.get<FlatListResponse<AdSetFlatNode>>('/accounts/facebook/adsets', { params }),

  listFacebookAds: (params: { brand_id: string; site_id?: string; account_id?: string; adset_id?: number; status_filter?: string; search?: string; page?: number; size?: number; platform?: 'all' | 'facebook' | 'google' | 'tiktok'; metric_filters?: string; start_date?: string; end_date?: string; sort_by?: string; sort_dir?: 'asc' | 'desc' }) =>
    api.get<FlatListResponse<AdFlatNode>>('/accounts/facebook/ads-list', { params }),

  toggleFacebookStatus: (params: {
    brand_id: string;
    site_id?: string;
    entity: 'campaign' | 'adset' | 'ad';
    id: number;
    pause: boolean;
  }) =>
    api.post<{ success: boolean; data?: FacebookPublishSummary }>('/accounts/facebook/toggle', params),

  toggleGoogleStatus: (params: {
    brand_id: string;
    site_id?: string;
    entity: 'campaign' | 'adset' | 'ad';
    id: number;
    pause: boolean;
  }) =>
    api.post<{ success: boolean }>('/accounts/google/toggle', params),

  toggleTikTokStatus: (params: {
    brand_id: string;
    site_id?: string;
    entity: 'campaign' | 'adset' | 'ad';
    id: number;
    pause: boolean;
  }) =>
    api.post<{ success: boolean }>('/accounts/tiktok/toggle', params),

  copyFacebookCampaign: (campaignID: number, data: CopyFacebookCampaignRequest) =>
    api.post<CampaignFlatNode>(`/accounts/facebook/campaigns/${campaignID}/copy`, data),

  copyFacebookAdSet: (adSetID: number, data: CopyFacebookAdSetRequest) =>
    api.post<AdSetFlatNode>(`/accounts/facebook/adsets/${adSetID}/copy`, data),

  copyFacebookAd: (adID: number, data: CopyFacebookAdRequest) =>
    api.post<AdFlatNode>(`/accounts/facebook/ads/${adID}/copy`, data),

  publishGoogleCampaign: (campaignID: number, data: { brand_id: string; site_id?: string }) =>
    api.post<{ entity: string; id: number; campaigns_published: number; adsets_published: number; ads_published: number }>(`/accounts/google/campaigns/${campaignID}/publish`, data),

  publishGoogleAdSet: (adSetID: number, data: { brand_id: string; site_id?: string }) =>
    api.post<{ entity: string; id: number; campaigns_published: number; adsets_published: number; ads_published: number }>(`/accounts/google/adsets/${adSetID}/publish`, data),

  publishGoogleAd: (adID: number, data: { brand_id: string; site_id?: string }) =>
    api.post<{ entity: string; id: number; campaigns_published: number; adsets_published: number; ads_published: number }>(`/accounts/google/ads/${adID}/publish`, data),

  copyGoogleCampaign: (campaignID: number, data: CopyGoogleCampaignRequest) =>
    api.post<CampaignFlatNode>(`/accounts/google/campaigns/${campaignID}/copy`, data),

  copyGoogleAdSet: (adSetID: number, data: CopyGoogleAdSetRequest) =>
    api.post<AdSetFlatNode>(`/accounts/google/adsets/${adSetID}/copy`, data),

  copyGoogleAd: (adID: number, data: CopyGoogleAdRequest) =>
    api.post<AdFlatNode>(`/accounts/google/ads/${adID}/copy`, data),

  publishTikTokCampaign: (campaignID: number, data: { brand_id: string; site_id?: string }) =>
    api.post<{ entity: string; id: number; campaigns_published: number; adsets_published: number; ads_published: number }>(`/accounts/tiktok/campaigns/${campaignID}/publish`, data),

  publishTikTokAdSet: (adSetID: number, data: { brand_id: string; site_id?: string }) =>
    api.post<{ entity: string; id: number; campaigns_published: number; adsets_published: number; ads_published: number }>(`/accounts/tiktok/adsets/${adSetID}/publish`, data),

  publishTikTokAd: (adID: number, data: { brand_id: string; site_id?: string }) =>
    api.post<{ entity: string; id: number; campaigns_published: number; adsets_published: number; ads_published: number }>(`/accounts/tiktok/ads/${adID}/publish`, data),

  copyTikTokCampaign: (campaignID: number, data: CopyTikTokCampaignRequest) =>
    api.post<CampaignFlatNode>(`/accounts/tiktok/campaigns/${campaignID}/copy`, data),

  copyTikTokAdSet: (adSetID: number, data: CopyTikTokAdSetRequest) =>
    api.post<AdSetFlatNode>(`/accounts/tiktok/adsets/${adSetID}/copy`, data),

  copyTikTokAd: (adID: number, data: CopyTikTokAdRequest) =>
    api.post<AdFlatNode>(`/accounts/tiktok/ads/${adID}/copy`, data),

  getFacebookCampaignEditDetail: (brandID: string, campaignID: number, siteID?: string) =>
    api.get<FacebookEditDetailResponse>(`/accounts/facebook/campaigns/${campaignID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateFacebookCampaign: (campaignID: number, data: { brand_id: string; site_id?: string; campaign: FacebookEditCampaignPayload }) =>
    api.put<FacebookEditDetailResponse>(`/accounts/facebook/campaigns/${campaignID}`, data, {
      params: { brand_id: data.brand_id, site_id: data.site_id },
    }),

  getFacebookAdSetEditDetail: (brandID: string, adSetID: number, siteID?: string) =>
    api.get<FacebookEditDetailResponse>(`/accounts/facebook/adsets/${adSetID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateFacebookAdSet: (adSetID: number, data: { brand_id: string; site_id?: string; campaign: FacebookEditCampaignPayload; adset: FacebookEditAdSetPayload; remote_fields?: string[] }) =>
    api.put<FacebookEditDetailResponse>(`/accounts/facebook/adsets/${adSetID}`, data, {
      params: { brand_id: data.brand_id, site_id: data.site_id },
    }),

  getFacebookAdEditDetail: (brandID: string, adID: number, siteID?: string) =>
    api.get<FacebookEditDetailResponse>(`/accounts/facebook/ads/${adID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateFacebookAd: (adID: number, data: { brand_id: string; site_id?: string; adset: FacebookEditAdSetPayload; ad: FacebookEditAdPayload }) =>
    api.put<FacebookEditDetailResponse>(`/accounts/facebook/ads/${adID}`, data, {
      params: { brand_id: data.brand_id, site_id: data.site_id },
    }),
  batchUpdatePlatformTags: (platform: 'facebook' | 'google' | 'tiktok', data: BatchUpdateFacebookTagsRequest) =>
    api.post<BatchUpdateFacebookTagsResponse>(`/accounts/${platform}/tags/batch`, data),

  createFacebookFull: (brandID: string, data: CreateFacebookFullRequest) =>
    api.post<CreateFacebookFullResponse>(`/accounts/facebook/create-full?brand_id=${encodeURIComponent(brandID)}`, data),

  listFacebookPages: (params: { brand_id: string; site_id?: string; account_id: string }) =>
    api.get<{ list: FacebookPage[] }>('/accounts/facebook/pages', { params }),

  verifyFacebookPage: (params: { brand_id: string; site_id?: string; account_id: string; page_id: string }) =>
    api.get<FacebookPage>('/accounts/facebook/pages/verify', { params }),

  listFacebookPixels: (params: { brand_id: string; site_id?: string; account_id: string }) =>
    api.get<{ list: FacebookPixel[] }>('/accounts/facebook/pixels', { params }),

  listFacebookCatalogs: (params: { brand_id: string; site_id?: string; account_id: string; refresh?: boolean }) =>
    api.get<{ list: FacebookCatalog[] }>('/accounts/facebook/catalogs', { params }),

  listFacebookProductSets: (params: { brand_id: string; site_id?: string; account_id: string; catalog_id: string; refresh?: boolean }) =>
    api.get<{ list: FacebookProductSet[] }>(`/accounts/facebook/catalogs/${params.catalog_id}/product-sets`, {
      params: { brand_id: params.brand_id, site_id: params.site_id, account_id: params.account_id, refresh: params.refresh },
    }),

  searchFacebookInterests: (params: { brand_id: string; site_id?: string; account_id: string; q: string }) =>
    api.get<{ list: FacebookInterest[] }>('/accounts/facebook/interests', { params }),

  listFacebookGeoLocations: (params: { brand_id: string; site_id?: string; account_id: string; refresh?: boolean }) =>
    api.get<{ list: FacebookGeoLocation[] }>('/accounts/facebook/geo-locations', { params }),

  listFacebookBehaviors: (params: { brand_id: string; site_id?: string; account_id: string; refresh?: boolean }) =>
    api.get<{ list: FacebookBehavior[] }>('/accounts/facebook/behaviors', { params }),

  generateAdPreview: (brandID: string, data: { account_id: string; creative_spec: Record<string, unknown>; ad_format?: string }) =>
    api.post<{ previews: { body: string }[] }>(`/accounts/facebook/ad-preview?brand_id=${encodeURIComponent(brandID)}`, data),

  listInstagramAccounts: (params: { brand_id: string; site_id?: string; account_id: string; refresh?: boolean }) =>
    api.get<{ list: FacebookInstagramAccount[] }>('/accounts/facebook/instagram-accounts', { params }),

  getVideoInfo: (params: { brand_id: string; site_id?: string; account_id: string; video_id: string }) =>
    api.get<{ video_id: string; ready: boolean; thumb_hash?: string }>('/accounts/facebook/video-info', { params }),

  listFacebookCustomAudiences: (params: { brand_id: string; site_id?: string; account_id: string }) =>
    api.get<{ list: FacebookCustomAudience[] }>('/accounts/facebook/custom-audiences', { params }),

  syncFacebookAssets: (params: { brand_id: string; site_id?: string; account_id: string }) =>
    api.post<{ ok: boolean }>(`/accounts/facebook/sync-assets?brand_id=${encodeURIComponent(params.brand_id)}&account_id=${encodeURIComponent(params.account_id)}${params.site_id ? `&site_id=${encodeURIComponent(params.site_id)}` : ''}`),

  validateFacebookFull: (brandID: string, data: CreateFacebookFullRequest) =>
    api.post<{ valid: boolean; campaign_error?: string; adset_error?: string; creative_error?: string }>(
      `/accounts/facebook/validate-full?brand_id=${encodeURIComponent(brandID)}`,
      data,
    ),

  getPartnershipInfo: (params: { brand_id: string; site_id?: string; account_id: string; page_id?: string; code: string }) =>
    api.get<FacebookPartnershipInfo>('/accounts/facebook/partnership-info', { params }),

  listFacebookPartnershipPages: (params: { brand_id: string; site_id?: string; account_id: string; page_id: string; instagram_account_id?: string; q?: string }) =>
    api.get<{ list: FacebookPartnershipPage[] }>('/accounts/facebook/partnership-pages', { params }),

  listFacebookPartnershipInstagramAccounts: (params: { brand_id: string; site_id?: string; account_id: string; instagram_account_id: string; q?: string }) =>
    api.get<{ list: FacebookPartnershipInstagramAccount[] }>('/accounts/facebook/partnership-instagram-accounts', { params }),

  listFacebookPosts: (params: { brand_id: string; site_id?: string; account_id: string; platform: 'facebook' | 'instagram'; q?: string; ig_user_id?: string }) =>
    api.get<{ list: FacebookExistingPost[] }>('/accounts/facebook/posts', { params }),

  getFacebookPostInfo: (params: { brand_id: string; site_id?: string; account_id: string; platform: 'facebook' | 'instagram'; post_id: string; page_id?: string; ig_user_id?: string }) =>
    api.get<FacebookExistingPost>('/accounts/facebook/posts/lookup', { params }),

  getFacebookInsights: (params: {
    brand_id: string;
    site_id?: string;
    entity: 'campaign' | 'adset' | 'ad';
    ids: string[];
    start_date?: string;
    end_date?: string;
  }) =>
    api.post<{ data: Record<string, EntityInsights> }>('/accounts/facebook/insights', {
      brand_id: params.brand_id,
      site_id: params.site_id,
      entity: params.entity,
      ids: params.ids,
      start_date: params.start_date,
      end_date: params.end_date,
    }),

  // ── TikTok Edit ──────────────────────────────────────────────────────────────

  getTikTokCampaignEditDetail: (brandID: string, campaignID: number, siteID?: string) =>
    api.get<TikTokEditDetailResponse>(`/accounts/tiktok/campaigns/${campaignID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateTikTokCampaign: (campaignID: number, data: { brand_id: string; site_id?: string; campaign: TikTokEditCampaignPayload; adset?: TikTokEditAdSetPayload }) =>
    api.put<TikTokEditDetailResponse>(`/accounts/tiktok/campaigns/${campaignID}`, data),

  getTikTokAdSetEditDetail: (brandID: string, adSetID: number, siteID?: string) =>
    api.get<TikTokEditDetailResponse>(`/accounts/tiktok/adsets/${adSetID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateTikTokAdSet: (adSetID: number, data: { brand_id: string; site_id?: string; campaign: TikTokEditCampaignPayload; adset: TikTokEditAdSetPayload }) =>
    api.put<TikTokEditDetailResponse>(`/accounts/tiktok/adsets/${adSetID}`, data),

  getTikTokAdEditDetail: (brandID: string, adID: number, siteID?: string) =>
    api.get<TikTokEditDetailResponse>(`/accounts/tiktok/ads/${adID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateTikTokAd: (adID: number, data: { brand_id: string; site_id?: string; adset: TikTokEditAdSetPayload; ad: TikTokEditAdPayload; remote_fields?: string[] }) =>
    api.put<TikTokEditDetailResponse>(`/accounts/tiktok/ads/${adID}`, data),

  // Create adgroup+ad under an existing TikTok campaign
  createTikTokAdGroupUnder: (campaignId: number, data: TikTokCreateAdFullRequest & { brand_id: string; site_id?: string }) =>
    api.post(`/accounts/tiktok/campaigns/${campaignId}/create-adgroup`, data),

  // Create ad under an existing TikTok adset
  createTikTokAdUnder: (adsetId: number, data: TikTokCreateAdFullRequest & { brand_id: string; site_id?: string }) =>
    api.post(`/accounts/tiktok/adsets/${adsetId}/create-ad`, data),

  // ── Google / TikTok Options ─────────────────────────────────────────────────

  searchGoogleGeoTargets: (params: { brand_id: string; account_id: string; keyword: string }) =>
    api.get<{ list: { id: string; name: string; country_code: string; target_type: string; canonical_name: string }[] }>('/accounts/google/geo-targets', { params }),

  searchGoogleLanguages: (params: { brand_id: string; account_id: string; keyword?: string }) =>
    api.get<{ list: { id: string; name: string; code: string }[] }>('/accounts/google/languages', { params }),

  getGoogleSnippetHeaders: () =>
    api.get<{ list: { value: string; label: string }[] }>('/accounts/google/snippet-headers'),

  searchTikTokRegions: (params: { brand_id: string; advertiser_id: string; keyword?: string; language?: string; objective_type?: string }) =>
    api.get<{ list: { location_id: string; name: string; level: string; parent_id?: string; region_code?: string }[] }>('/accounts/tiktok/regions', { params }),

  listTikTokPixels: (params: { brand_id: string; site_id?: string; advertiser_id: string }) =>
    api.get<{ list: TikTokPixel[] }>('/accounts/tiktok/pixels', { params }),

  listTikTokAudiences: (params: { brand_id: string; site_id?: string; advertiser_id: string }) =>
    api.get<{ list: TikTokAudience[] }>('/accounts/tiktok/audiences', { params }),

  listTikTokTargetingSearch: (params: { brand_id: string; site_id?: string; advertiser_id: string; language?: string; keyword?: string }) =>
    api.get<{ list: TikTokTargetingSearchItem[] }>('/accounts/tiktok/targeting-search', { params }),

  listTikTokInterestCategories: (params: { brand_id: string; advertiser_id: string; language?: string; keyword?: string }) =>
    api.get<{ list: TikTokInterestCategory[] }>('/accounts/tiktok/interest-categories', { params }),

  listTikTokActionCategories: (params: { brand_id: string; advertiser_id: string; language?: string }) =>
    api.get<{ list: TikTokActionCategory[] }>('/accounts/tiktok/action-categories', { params }),

  getTikTokOptimizationEvents: (params: {
    brand_id: string;
    site_id?: string;
    advertiser_id: string;
    objective_type: string;
    optimization_goal: string;
    pixel_id?: string;
  }) =>
    api.get<{ list: TikTokOptimizationEventItem[] }>('/accounts/tiktok/optimization-events', { params }),

  listTikTokCatalogs: (params: { brand_id: string; site_id?: string; advertiser_id: string; bc_id: string }) =>
    api.get<{ list: TikTokCatalog[] }>('/accounts/tiktok/catalogs', { params }),

  listTikTokCatalogSets: (params: { brand_id: string; site_id?: string; advertiser_id: string; bc_id: string; catalog_id: string }) =>
    api.get<{ list: TikTokCatalogSet[] }>('/accounts/tiktok/catalog-sets', { params }),

  listTikTokVideos: (params: { brand_id: string; site_id?: string; advertiser_id: string }) =>
    api.get<{ list: TikTokVideoItem[] }>('/accounts/tiktok/videos', { params }),

  listTikTokPosts: (params: {
    brand_id: string;
    site_id?: string;
    advertiser_id: string;
    identity_id?: string;
    identity_type?: string;
    identity_authorized_bc_id?: string;
    cursor?: string;
    keyword?: string;
  }) =>
    api.get<TikTokSparkPostListResponse>('/accounts/tiktok/posts', { params }),

  getTikTokSparkPostInfo: (params: { brand_id: string; site_id?: string; advertiser_id: string; auth_code: string }) =>
    api.post<TikTokSparkPostInfo>(`/accounts/tiktok/spark-post-info?brand_id=${encodeURIComponent(params.brand_id)}&advertiser_id=${encodeURIComponent(params.advertiser_id)}${params.site_id ? `&site_id=${encodeURIComponent(params.site_id)}` : ''}`, { auth_code: params.auth_code }),

  // ── Google Edit ─────────────────────────────────────────────────────────────

  getGoogleCampaignEditDetail: (brandID: string, campaignID: number, siteID?: string) =>
    api.get<GoogleEditDetailResponse>(`/accounts/google/campaigns/${campaignID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateGoogleCampaign: (campaignID: number, data: { brand_id: string; site_id?: string; campaign: GoogleEditCampaignPayload }) =>
    api.put<GoogleEditDetailResponse>(`/accounts/google/campaigns/${campaignID}`, data),

  getGoogleAdSetEditDetail: (brandID: string, adSetID: number, siteID?: string) =>
    api.get<GoogleEditDetailResponse>(`/accounts/google/adsets/${adSetID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateGoogleAdSet: (adSetID: number, data: { brand_id: string; site_id?: string; campaign: GoogleEditCampaignPayload; adset: GoogleEditAdSetPayload }) =>
    api.put<GoogleEditDetailResponse>(`/accounts/google/adsets/${adSetID}`, data),

  getGoogleAdEditDetail: (brandID: string, adID: number, siteID?: string) =>
    api.get<GoogleEditDetailResponse>(`/accounts/google/ads/${adID}/edit`, { params: { brand_id: brandID, site_id: siteID } }),

  updateGoogleAd: (adID: number, data: { brand_id: string; site_id?: string; adset: GoogleEditAdSetPayload; ad: GoogleEditAdPayload }) =>
    api.put<GoogleEditDetailResponse>(`/accounts/google/ads/${adID}`, data),

  // ── 通用多平台接口 ──────────────────────────────────────────────────────────

  /** 通用多平台广告创建（platform 字段 + 平台参数扁平合并） */
  createAdFull: (brandID: string, data: CreateAdFullRequest) =>
    api.post<AdCreateFullResult>(`/accounts/ads/create-full?brand_id=${encodeURIComponent(brandID)}`, data),

  /** 通用多平台广告验证（platform 字段 + 平台参数扁平合并） */
  validateAdFull: (brandID: string, data: ValidateAdFullRequest) =>
    api.post<AdValidateResult>(`/accounts/ads/validate-full?brand_id=${encodeURIComponent(brandID)}`, data),

  saveManualDraft: (brandID: string, data: DraftPayloadRequest) =>
    api.post<DraftPayloadResponse>(`/accounts/drafts/manual?brand_id=${encodeURIComponent(brandID)}`, data),

  getManualDraft: (params: { brand_id: string; site_id?: string; platform: 'facebook' | 'tiktok' | 'google'; scene: 'create' | 'edit'; entity_type: string; entity_id?: number; draft_key: string }) =>
    api.get<DraftPayloadResponse>('/accounts/drafts/manual', { params }),

  saveTempDraft: (brandID: string, data: DraftPayloadRequest) =>
    api.post<DraftPayloadResponse>(`/accounts/drafts/temp?brand_id=${encodeURIComponent(brandID)}`, data),

  getTempDraft: (params: { brand_id: string; site_id?: string; platform: 'facebook' | 'tiktok' | 'google'; scene: 'create' | 'edit'; entity_type: string; entity_id?: number; draft_key: string }) =>
    api.get<DraftPayloadResponse>('/accounts/drafts/temp', { params }),

  deleteTempDraft: (params: { brand_id: string; site_id?: string; platform: 'facebook' | 'tiktok' | 'google'; scene: 'create' | 'edit'; entity_type: string; entity_id?: number; draft_key: string }) =>
    api.delete<{ success: boolean }>('/accounts/drafts/temp', { params }),

  listSavedViews: (params: { brand_id: string; site_id?: string; platform_scope: 'facebook' | 'tiktok' | 'google' }) =>
    api.get<{ list: SavedViewItemResponse[] }>('/accounts/views', { params }),

  saveSavedView: (brandID: string, data: { site_id?: string; platform_scope: 'facebook' | 'tiktok' | 'google'; name: string; draft: SavedViewDraftPayload }) =>
    api.post<SavedViewItemResponse>(`/accounts/views?brand_id=${encodeURIComponent(brandID)}`, data),

  deleteSavedView: (params: { brand_id: string; site_id?: string; platform_scope: 'facebook' | 'tiktok' | 'google'; name: string }) =>
    api.delete<{ ok: boolean }>('/accounts/views', { params }),
};

// ============================================
// TikTok Types
// ============================================

export interface TikTokPixel {
  pixel_id: string;
  pixel_name: string;
  status?: string;
  optimization_events?: string[];
}

export interface TikTokAudience {
  audience_id: string;
  audience_name: string;
  audience_type?: string;
  cover_num?: number;
}

export interface TikTokInterestCategory {
  id: string;
  name: string;
  parent_id?: string;
  level?: number;
}

export interface TikTokTargetingSearchItem {
  id: string;
  name: string;
  parent_id?: string;
  level?: number;
  action_scene?: string;
  category?: string;
  sub_type?: string;
  target_field?: string;
}

export interface TikTokActionCategory {
  id: string;
  name: string;
  parent_id?: string;
  level?: number;
  action_scene?: string;
}

export interface TikTokOptimizationEventItem {
  external_action: string;
  availability_status: string;
  conversions: number;
  unlock_threshold: number;
  is_beta: boolean;
}

export interface TikTokCatalog {
  catalog_id: string;
  catalog_name: string;
  product_num?: number;
  country?: string;
  country_code?: string;
  raw?: Record<string, unknown>;
}

export interface TikTokCatalogSet {
  store_id: string;
  store_name: string;
  product_set_id?: string;
  product_set_name?: string;
  product_num?: number;
}

export interface TikTokVideoItem {
  video_id: string;
  display_name?: string;
  duration?: number;
  preview_url?: string;
  cover_url?: string;
  source: string; // "library" | "tiktok_page"
}

export interface TikTokSparkPost {
  item_id: string;
  video_id?: string;
  caption?: string;
  cover_url?: string;
  preview_url?: string;
  display_name?: string;
  source?: string;
  create_time?: string;
  duration?: number;
  width?: number;
  height?: number;
}

export interface TikTokSparkPostListResponse {
  list: TikTokSparkPost[];
  has_more?: boolean;
  cursor?: string;
}

export interface TikTokSparkPostInfo {
  item_id: string;
  video_id?: string;
  caption?: string;
  cover_url?: string;
  preview_url?: string;
  display_name?: string;
  status?: string;
  identity_id?: string;
  identity_type?: string;
}

export interface TikTokCreateAdFullRequest {
  platform: 'tiktok';
  advertiser_id: string;
  // Campaign
  campaign_name: string;
  objective_type: string;
  campaign_budget_mode: string;
  campaign_budget_strategy?: string;
  campaign_budget?: number;
  budget_optimize_on?: boolean;
  campaign_status?: string;
  special_industries?: string[];
  roas_bid?: number;
  campaign_product_source?: string;
  sales_destination?: string;
  catalog_enabled?: boolean;
  // AdGroup
  adgroup_name: string;
  audience_type?: string;
  audience_ids?: string[];
  excluded_audience_ids?: string[];
  saved_audience_id?: string;
  adgroup_budget_mode?: string;
  adgroup_budget?: number;
  schedule_type?: string;
  start_time?: string;
  end_time?: string;
  placement_type?: string;
  placements?: string[];
  optimization_goal: string;
  bid_type?: string;
  bid_price?: number;
  billing_event?: string;
  brand_safety_type?: string;
  brand_safety_partner?: string;
  pixel_id?: string;
  optimization_event?: string;
  search_result_enabled?: boolean;
  search_keywords?: string[];
  location_ids?: string[];
  age_groups?: string[];
  gender?: string;
  languages?: string[];
  interest_category_ids?: number[];
  video_action_category_ids?: string[];
  interest_behavior_items?: TikTokTargetingSearchItem[];
  operating_systems?: string[];
  network_types?: string[];
  identity_type?: string;
  identity_id?: string;
  identity_authorized_bc_id?: string;
  creative_material_mode?: string;
  app_id?: string;
  promotion_type?: string;
  promotion_target_type?: string;
  pacing?: string;
  frequency?: number;
  frequency_schedule?: number;
  click_attribution_window?: string;
  view_attribution_window?: string;
  statistic_type?: string;
  product_source?: string;
  catalog_id?: string;
  catalog_authorized_bc_id?: string;
  store_id?: string;
  store_authorized_bc_id?: string;
  shopping_ads_type?: string;
  product_specific_type?: string;
  product_set_id?: string;
  adgroup_status?: string;
  // Ad
  ad_name: string;
  ad_format: string;
  ad_text: string;
  ad_texts?: string[];
  display_name?: string;
  auth_code?: string;
  call_to_action?: string;
  landing_page_url?: string;
  video_id?: string;
  video_ids?: string[];
  tiktok_item_id?: string;
  tiktok_item_ids?: string[];
  ad_status?: string;
  music_id?: string;
  video_cover_url?: string;
  post_cover_url?: string;
  post_caption?: string;
  // 广告标签（动态 key-value，分层级）
  tags?: Record<string, string>;
  adset_tags?: Record<string, string>;
  ad_tags?: Record<string, string>;
}

export interface TikTokIdentityOption {
  identity_id: string;
  identity_type: string;
  display_name?: string;
  username?: string;
  identity_authorized_bc_id?: string;
}

export interface TikTokAdResult {
  id: number;
  third_ad_id: string;
  name: string;
  publish_status: string;
  publish_error?: string;
}

export interface CreateTikTokFullResponse {
  campaign_local_id: number;
  campaign_third_id: string;
  campaign_name: string;
  campaign_error?: string;
  adgroup_local_id: number;
  adgroup_third_id: string;
  adgroup_name: string;
  adgroup_error?: string;
  ad?: TikTokAdResult;
}

// ============================================
// TikTok Edit Types
// ============================================

export interface TikTokEditCampaignPayload {
  name: string;
  objective_type: string;
  budget_strategy: string;
  budget_mode: string;
  budget: number;
  operation_status: string;
  special_industries: string[];
  roas_bid?: number;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface TikTokEditAdSetPayload {
  name: string;
  adgroup_status: string;
  budget_mode: string;
  budget: number;
  schedule_type: string;
  start_time: string;
  end_time: string;
  placement_type: string;
  placements: string[];
  optimization_goal: string;
  bid_type: string;
  bid_price: number;
  billing_event: string;
  pixel_id: string;
  optimization_event: string;
  audience_ids: string[];
  excluded_audience_ids: string[];
  location_ids: string[];
  age_groups: string[];
  gender: string;
  languages: string[];
  interest_category_ids: string[];
  video_action_category_ids: string[];
  interest_behavior_items?: TikTokTargetingSearchItem[];
  operating_systems: string[];
  network_types: string[];
  identity_type: string;
  identity_id: string;
  creative_material_mode: string;
  app_id: string;
  promotion_type: string;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface TikTokEditAdSet {
  id: number;
  campaign_id: number;
  name: string;
  adgroup_status: string;
  budget_mode: string;
  budget: number;
  schedule_type: string;
  start_time: string;
  end_time: string;
  placement_type: string;
  placements: string[];
  optimization_goal: string;
  bid_type: string;
  bid_price: number;
  billing_event: string;
  pixel_id: string;
  optimization_event: string;
  audience_ids: string[];
  excluded_audience_ids: string[];
  location_ids: string[];
  age_groups: string[];
  gender: string;
  languages: string[];
  interest_category_ids: string[];
  video_action_category_ids: string[];
  operating_systems: string[];
  network_types: string[];
  identity_type: string;
  identity_id: string;
  creative_material_mode: string;
  app_id: string;
  promotion_type: string;
  tags?: Record<string, string>;
}

export interface TikTokEditAdPayload {
  name: string;
  ad_status: string;
  ad_format: string;
  ad_text: string;
  call_to_action: string;
  landing_page_url: string;
  video_id: string;
  video_ids_json?: string;
  image_ids_json?: string;
  cover_image_hash?: string;
  display_name?: string;
  avatar_icon_web_uri?: string;
  identity_type: string;
  identity_id: string;
  identity_authorized_bc_id?: string;
  tiktok_item_id: string;
  tiktok_item_ids_json?: string;
  auth_code?: string;
  video_cover_url?: string;
  post_cover_url?: string;
  post_caption?: string;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface TikTokEditDetailResponse {
  entity: 'campaign' | 'adset' | 'ad';
  editable_fields: string[];
  sync_mode?: string;
  campaign?: TikTokEditCampaignPayload & { id: number; account_id: string };
  adset?: TikTokEditAdSetPayload & { id: number; campaign_id: number };
  ad?: TikTokEditAdPayload & { id: number; adset_id: number };
}

// ============================================
// Google Edit Types
// ============================================

export interface GoogleEditCampaignPayload {
  name: string;
  objective: string;
  campaign_status: string;
  daily_budget_micros: number;
  start_date: string;
  end_date: string;
  target_google_search: boolean;
  target_search_network: boolean;
  target_partner_search_network: boolean;
  target_content_network: boolean;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface GoogleEditAdSetPayload {
  name: string;
  adset_status: string;
  cpc_bid_micros: number;
  keywords_json: string;
  location_ids_json: string;
  language_id: string;
  locations?: { id: string; name: string }[];
  languages?: { id: string; name: string; code: string }[];
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface GoogleEditAdPayload {
  name: string;
  ad_status: string;
  final_url: string;
  headlines_json: string;
  descriptions_json: string;
  path1: string;
  path2: string;
  // 动态标签（来自 ad_tag_config）
  tags?: Record<string, string>;
}

export interface GoogleEditDetailResponse {
  entity: 'campaign' | 'adset' | 'ad';
  editable_fields: string[];
  sync_mode?: string;
  campaign?: GoogleEditCampaignPayload & { id: number; account_id: string };
  adset?: GoogleEditAdSetPayload & { id: number; campaign_id: number };
  ad?: GoogleEditAdPayload & { id: number; adset_id: number };
}
